import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  CalendarDayGroup,
  CalendarEvent,
  CalendarEventCategory,
  CalendarEventStatus,
  CalendarSettings,
  DEFAULT_CALENDAR_SETTINGS,
} from '../../models/calendar.types';
import { CalendarExportService } from '../../services/calendar-export.service';
import { formatTimeRange, groupEventsByDay } from '../../utils/calendar-date.utils';

@Component({
  selector: 'aura-calendar-list-view',
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './calendar-list-view.component.html',
  host: {
    class: 'flex flex-col flex-1 min-h-0 w-full h-full',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarListViewComponent {
  private readonly exportService = inject(CalendarExportService);

  readonly currentDate = input.required<Date>();
  readonly events = input<CalendarEvent[]>([]);
  readonly settings = input<CalendarSettings>(DEFAULT_CALENDAR_SETTINGS);
  readonly emptyMessage = input<string>('Aucun rendez-vous prévu pour le moment.');

  readonly eventClick = output<CalendarEvent>();

  readonly filteredEvents = computed<CalendarEvent[]>(() => {
    const evts = this.events();
    if (this.settings().showWeekends) return evts;
    return evts.filter((e) => {
      const d = new Date(e.startAt);
      const day = d.getDay();
      return day !== 0 && day !== 6;
    });
  });

  readonly filterStartDate = computed(() => {
    const d = this.currentDate();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  readonly filterEndDate = computed(() => {
    const d = this.currentDate();
    return new Date(d.getFullYear(), d.getMonth() + 1, 0);
  });

  readonly groupedEvents = computed<CalendarDayGroup[]>(() =>
    groupEventsByDay(this.filteredEvents(), this.filterStartDate(), this.filterEndDate()),
  );

  getTimeFormatted(evt: CalendarEvent): string {
    if (evt.allDay) return 'Toute la journée';
    return formatTimeRange(evt.startAt, evt.endAt);
  }

  getStatusBadge(status?: CalendarEventStatus): { label: string; class: string } {
    switch (status) {
      case 'confirmed':
        return {
          label: 'Confirmé',
          class: 'bg-jade-3 text-jade-11 border-jade-6',
        };
      case 'tentative':
        return {
          label: 'En attente',
          class: 'bg-amber-3 text-amber-11 border-amber-6',
        };
      case 'completed':
        return {
          label: 'Effectué',
          class: 'bg-primary-3 text-primary-11 border-primary-6',
        };
      case 'cancelled':
        return {
          label: 'Annulé',
          class: 'bg-ruby-3 text-ruby-11 border-ruby-6',
        };
      default:
        return {
          label: 'Prévu',
          class: 'bg-neutral-3 text-neutral-11 border-neutral-6',
        };
    }
  }

  getBorderColor(category?: CalendarEventCategory): string {
    switch (category) {
      case 'jade':
        return 'border-l-jade-9';
      case 'amber':
        return 'border-l-amber-9';
      case 'ruby':
        return 'border-l-ruby-9';
      case 'violet':
        return 'border-l-violet-9';
      case 'indigo':
        return 'border-l-indigo-9';
      case 'primary':
      default:
        return 'border-l-primary-9';
    }
  }
}
